import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useMemo, useState } from 'react';
import { ChatDialogue, ChatDialogueProps } from './ChatDialogue';
import MediaPreview from '../MediaPreview/MediaPreview';
import { ChatMessageEditor } from './ChatMessageEditor';

type TabKey = 'buyer' | 'mercado';

interface TabListProps {
  items: Array<{ label: string; tabKey: TabKey }>;
  activeKey: TabKey;
  onChange: (activeKey: TabKey) => void;
}

const TabList = (props: TabListProps) => {
  const { items, activeKey, onChange } = props;
  return (
    <div className="flex gap-6 items-center border-0 border-b border-fill-300 border-solid">
      {items.map(({ label, tabKey }) => (
        <TabItem
          key={tabKey}
          className={cx([
            'text-content-300 flex-1 flex justify-center cursor-pointer',
            activeKey === tabKey ? 'active' : undefined,
          ])}
          onClick={() => onChange(tabKey)}
        >
          <div className="relative h-10 leading-10">{label}</div>
        </TabItem>
      ))}
    </div>
  );
};

interface MercadoMediationChatProps extends ChatDialogueProps {
  chatAlert: JSX.Element;
  contextMenu: JSX.Element | null;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
}

export const MercadoMediationChat = (props: MercadoMediationChatProps) => {
  const { chatAlert, contextMenu, conversation, forceAcquireControlReason, ...chatDialogueProps } =
    props;
  // 当前是否在调解阶段
  const { mediation, hadMediation } = conversation;
  const { t } = useTranslate();

  const [activeTabKey, setActiveTabKey] = useState<TabKey>(mediation ? 'mercado' : 'buyer');

  const onMercadoChatView = activeTabKey === 'mercado';

  const forceDisableText = useMemo(() => {
    if (mediation && !onMercadoChatView) {
      return t('Mercado介入调解中，暂不可回复');
    }
    if (!mediation && hadMediation && onMercadoChatView) {
      return t('争议已结束');
    }
    return undefined;
  }, [mediation, hadMediation, onMercadoChatView, t]);

  return (
    <div className="w-full flex-1 flex flex-col">
      <TabList
        items={[
          {
            label: t('与买家对话'),
            tabKey: 'buyer',
          },
          {
            label: t('与Mercado对话'),
            tabKey: 'mercado',
          },
        ]}
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
      />
      <div className="flex-1 flex flex-col" key={activeTabKey}>
        {chatAlert}
        <ChatDialogue
          conversation={conversation}
          onMercadoChatView={onMercadoChatView}
          {...chatDialogueProps}
        />
        <ChatMessageEditor
          conversation={conversation}
          forceAcquireControlReason={forceAcquireControlReason}
          showSideFoldBtn={false}
          onMercadoChatView={onMercadoChatView}
          forceDisableText={forceDisableText}
        />
        {contextMenu}
        <MediaPreview />
      </div>
    </div>
  );
};

const TabItem = styled.div`
  &.active {
    color: var(--fett-app-primary-color);
    font-weight: 600;

    & > div:after {
      content: '';
      display: block;
      position: absolute;
      height: 2px;
      width: 100%;
      background-color: var(--fett-app-primary-color);
    }
  }
`;
